import React, { FormEventHandler, useState } from 'react';
import { CognitoUser } from 'amazon-cognito-identity-js';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import LogoBlack from '../LogoBlack';
import * as UI from '../common';
import { isPasswordValid } from '../../utils';
import { PinCode } from '../common';
import { Alert } from 'flowbite-react';
import { InputComponent } from '../core/Forms';
import userPool from '../../utils/userPool';
import { useCognitoLogin } from '../../hooks';

interface FormData {
  password: string;
  confirmPassword: string;
}

interface Errors {
  password: string;
  confirmPassword: string;
}

interface ValidationResults {
  confirmPassword: string;
}

const ResetPasswordView = () => {
  const { authenticateUser, isLoading: isLoginLoading, errorMessage: message } = useCognitoLogin();

  const navigate = useNavigate();
  const location = useLocation();

  const searchParams = new URLSearchParams(location.search);
  const email = searchParams.get('email');
  if (!email) throw new Error('user not found');

  const [code, setCode] = useState<string>('');
  const [isLoading, setIsLoading] = useState(false);
  const [disabled, setDisabled] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [buttonText] = useState('Reset Password');
  const [attempts, setAttempts] = useState(0);


  const [formData, setFormData] = useState<FormData>({
    password: '',
    confirmPassword: '',
  });

  const [errors, setErrors] = useState<Errors>({
    password: '',
    confirmPassword: '',
  });

  const [validationResults, setValidationResults] = useState<ValidationResults>({
    confirmPassword: '',
  });

  const handleInputChange = (event: any) => {
    const { name, value } = event.target;
    setFormData({ ...formData, [name]: value });
    if (name === 'password' && !isPasswordValid(value)) {
      setErrors({ ...errors, password: 'New Password is not valid' });
    } else {
      setErrors({ ...errors, password: '' });
    }
    if (name === 'confirmPassword' && !value) {
      setErrors({ ...errors, confirmPassword: 'Confirm the Password' });
    } else {
      setErrors({ ...errors, confirmPassword: '' });
    }
  };

  const handleResetPassword: FormEventHandler<HTMLFormElement> = async (e) => {
    e.preventDefault();
    // if (!dirty && !disabled) {
    //   setDirty(true);
    // }
    if (!dirty && attempts >= 1) {
      return;
    }
    setAttempts(attempts + 1);
    setIsLoading(true);
    setDisabled(true);
    if (Object.values(errors).some((error) => error)) {
      setErrorMessage('Please correct the errors before submitting.');
      setIsLoading(false);
      setDisabled(false);
      return;
    }

    const user = new CognitoUser({Username: email, Pool: userPool});

    user.confirmPassword(code, formData.password, {
      async onSuccess() {
        await authenticateUser(email, formData.password);
        setIsLoading(false);
      },
      onFailure(err) {
        const message = err?.message || 'There was an error resetting your password. Please try again.';
        setErrorMessage(message);
        setIsLoading(false);
        setDisabled(false);
      },
    });
  }

  return (
    <>
      <div className=" flex justify-center items-center lg:flex pt-[100px] lg:pt-0  lg:justify-center lg:items-center">
        <div className="w-[295px] md:w-[450px] lg:w-[578px] lg:h-[847px] lg:px-[60px] border lg:bg-white lg:border lg:mx-40 lg:my-40 lg:px-[48px] lg:text-[#1C3041]">
          <div>
            <div className="flex justify-center my-[20px] xl:my-0 lg:flex lg:justify-center lg:pt-[56px]">
              <LogoBlack />
            </div>

            <div className="lg:pb-[19.05px] text-center lg:text-center ">
              <h1 className="lg:text-[32px] text-[20px] font-bold flex flex-col text-center  lg:font-bold lg:flex-col lg:text-center lg:pt-[47px]">Reset Password</h1>
              <p className="lg:pb-[23px] text-[16px] lg:text-[16px]">To reset your password, we need you to create a new password.</p>

              {errorMessage ? <Alert color="failure">{errorMessage}</Alert> : null}
            </div>
          </div>
          <div>
            <div className=" lg:flex-col lg:items-center lg:justify-center lg:min-h-screen ">
              <div className="lg:w-full lg:px-6 lg:rounded-lg ">
                {errorMessage && <Alert color="danger">{errorMessage}</Alert>}

                <form onSubmit={handleResetPassword}>
                  <div className="">
                    <div className='w-full h-full mt-[15px] mb-[10px] xl:mt-0 xl:mb-0'>
                      <PinCode codeLength={6} onCodeComplete={setCode} />
                    </div>
                    
                    <div className="mt-[2em] pl-[12px] md:pl-[22px] xl:pl-0">
                      <label htmlFor="Password" className="lg:block lg:font-sm lg:font-weight-[400] lg:text-[13px] lg:text-black ">
                        Enter New Password
                      </label>
                      <InputComponent
                        placeholder="Enter Password"
                        name="password"
                        type="password"
                        value={formData.password}
                        handleChange={handleInputChange}
                        handleOnInput={handleInputChange}
                        handleOnFocus={handleInputChange}
                        handleOnBlur={handleInputChange}
                        className="lg:w-[434px] w-[270px] md:w-[400px]"
                        imageClassName="ml-[21em] 375:ml-[15em]  414:ml-[15em] 360:ml-[15em] 412:ml-[15em] mt-[-5em] md:ml-[22em] md:mt-[-5.5em]  lg:ml-[25em] lg:mt-[-5em] xl:ml-[25em] xl:mt-[-5em] 540:ml-[15em] 540:mt-[-4.5em]"
                      />
                    </div>

                    <div className="mt-[2em] pl-[12px] md:pl-[22px] xl:pl-0">
                      <label htmlFor="Password" className="block font-sm font-weight-[400] text-[13px] text-black ">
                        Confirm Password
                      </label>
                      <InputComponent
                        placeholder="Confirm Password"
                        name="confirmPassword"
                        type="password"
                        value={formData.confirmPassword}
                        handleChange={handleInputChange}
                        handleOnInput={handleInputChange}
                        handleOnFocus={handleInputChange}
                        handleOnBlur={handleInputChange}
                        className="lg:w-[434px] w-[270px] md:w-[400px]"
                        error={validationResults.confirmPassword || errors.confirmPassword}
                        imageClassName="ml-[21em] 375:ml-[15em]  414:ml-[15em] 360:ml-[15em] 412:ml-[15em] mt-[-5em] md:ml-[22em] md:mt-[-5.5em]  lg:ml-[25em] lg:mt-[-5em] xl:ml-[25em] xl:mt-[-5em] 540:ml-[15em] 540:mt-[-4.5em]"
                      />
                    </div>


                    <div className="lg:w-full lg:h-[48px] mt-[15px] lg:flex lg:mt-[2em] flex justify-center px-[31px] lg:justify-center">
                      <UI.Button
                      disabled={attempts >= 1}
                        text={buttonText}
                        isLoading={isLoading}
                      />
                    </div>
                    <div className="lg:flex lg:justify-center flex justify-center lg:mt-[-em]">
                      <Link
                        to="/"
                        className=" lg:flex lg:text-center lg:py-[13px] lg:pb-[26px] py-[13px] pb-[26px] lg:font-sans lg:font-italic lg:font-semibold lg:text-base lg:leading-none lg:underline underline text-blue-700 lg:text-blue-700"
                      >
                        <i>Go back to sign up</i>{' '}
                      </Link>
                    </div>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};
export { ResetPasswordView };


